import {
  CountryCodeDropDown,
  VisibilityOff,
  VisibilityOn,
} from '@zappyrent/hundop';
import React, { useEffect, useRef, useState } from 'react';

import {
  CountryCodeDropDownWrapper,
  StyledInput,
  StyledLabel,
  StyledSwitch,
  StyledWrapper,
} from './TextInput.styles';
import { ITextInputProps } from './TextInput.types';

const TextInput = ({
  ariaLabel = 'Show/hide password',
  children,
  disabled = false,
  id,
  label,
  name,
  type = 'text',
  user = 'neutral',
  value,
  showEuroIcon = false,
  validations = {},
  required = false,
  minValue,
  maxValue,
  onChange,
  onFocus,
  onBlur,
}: ITextInputProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isActive, setIsActive] = useState<boolean>(
    false || type === 'date' || type === 'number',
  );
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value);
  const [inputType, setInputType] = useState<string>(type);
  const [currentCountryCode, setCurrentCountryCode] = useState<string>('');

  const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsActive(true);
    onFocus && onFocus(event);
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    !isEmpty && type !== 'date' && setIsActive(false);
    onBlur && onBlur(event);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    onChange && onChange(event);
  };

  const handleSwitch = () => {
    setInputType(inputType === 'text' ? 'password' : 'text');
  };

  const handleCountryCodeChange = (
    countryCode: string,
    canFocus?: boolean,
  ): void => {
    setCurrentCountryCode(countryCode);
    setInputValue(
      `${countryCode} ${inputValue.replace(currentCountryCode, '')}`,
    );
    if (canFocus) inputRef.current && inputRef.current.focus();
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!isActive) {
      setIsActive(!!inputValue);
    }
    setIsEmpty(!inputValue || inputValue === '');
  }, [inputValue]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <StyledWrapper
      isActive={isActive}
      isEmpty={isEmpty}
      hasCountryCodeDropDown={type === 'tel'}
      inputValue={inputValue}
      showEuroIcon={showEuroIcon}
    >
      {label && (
        <StyledLabel htmlFor={id} required={required}>
          {label}
        </StyledLabel>
      )}
      {type === 'tel' && (
        <CountryCodeDropDownWrapper>
          <CountryCodeDropDown
            id={id}
            selectedCountry="IT"
            onChange={handleCountryCodeChange}
          />
        </CountryCodeDropDownWrapper>
      )}
      <StyledInput
        disabled={disabled}
        id={id}
        name={name}
        ref={inputRef}
        type={inputType}
        user={user}
        value={inputValue}
        onChange={handleOnChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onKeyDown={handleKeyDown}
        min={minValue}
        max={maxValue}
        {...validations}
      />
      {type === 'password' && (
        <StyledSwitch onClick={handleSwitch} title={ariaLabel} type="button">
          {inputType === 'password' && <VisibilityOff />}
          {inputType === 'text' && <VisibilityOn />}
        </StyledSwitch>
      )}
      {children}
    </StyledWrapper>
  );
};

export default TextInput;
